import { useState, type FormEvent, type ReactNode } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import { browserTimezone, useAuth } from "@/features/auth/AuthContext";
import { ApiError, post } from "@/shared/api/client";
import { BRAND_STATEMENT, LogoMark, TAGLINE, Wordmark } from "@/shared/ui/brand";
import { InlineError, useDocumentTitle } from "@/shared/ui/layout";
import { Button, Field, Input } from "@/shared/ui/primitives";

function AuthLayout({ title, subtitle, children, footer }:
  { title: string; subtitle?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  useDocumentTitle(title);
  return (
    <div className="grid min-h-dvh lg:grid-cols-[1.05fr_1fr]">
      <aside className="relative hidden overflow-hidden bg-accent-soft p-12 lg:flex lg:flex-col lg:justify-between">
        <Wordmark />
        <div className="max-w-md">
          <LogoMark className="mb-8 size-16" title="" />
          <p className="font-title text-[40px] leading-[1.1] font-semibold text-ink">{TAGLINE}</p>
          <p className="mt-4 text-[17px] text-ink-2">{BRAND_STATEMENT}</p>
          <ul className="mt-10 space-y-3 text-[15px] text-ink-2">
            <li>• A realistic daily plan built around the time you actually have.</li>
            <li>• Trustworthy free resources from universities and open textbooks.</li>
            <li>• Honest measures of what you've mastered — not just what you've clicked.</li>
          </ul>
        </div>
        <p className="text-[13px] text-ink-3">Self-study builds real skills; it doesn't award degrees or credentials.</p>
      </aside>
      <main className="flex items-center justify-center px-4 py-12 sm:px-8">
        <div className="w-full max-w-[400px]">
          <div className="mb-10 lg:hidden"><Wordmark /></div>
          <h1 className="font-title text-[30px] leading-tight font-semibold text-ink">{title}</h1>
          {subtitle && <p className="mt-2 text-[15px] text-ink-2">{subtitle}</p>}
          <div className="mt-8">{children}</div>
          {footer && <div className="mt-8 text-[14px] text-ink-2">{footer}</div>}
        </div>
      </main>
    </div>
  );
}

export function SignInPage() {
  const { user, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [expired] = useState(() => sessionStorage.getItem("pl-expired") === "1");
  const from = (location.state as { from?: string } | null)?.from ?? "/today";
  if (user) return <Navigate to={from} replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
      sessionStorage.removeItem("pl-expired");
      navigate(from, { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to continue your learning journey."
      footer={<>New to Person Learn? <Link to="/register" className="font-semibold text-accent-text hover:underline">Create an account</Link></>}>
      {expired && (
        <p className="mb-6 rounded-xl bg-info-soft px-3 py-2 text-[14px] text-info">Your session ended. Please sign in again — your work was saved.</p>
      )}
      <form onSubmit={submit} className="space-y-5" noValidate>
        <Field label="Email" htmlFor="email">
          <Input id="email" type="email" autoComplete="email" required value={email}
            onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Password" htmlFor="password">
          <Input id="password" type="password" autoComplete="current-password" required value={password}
            onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <InlineError error={error} />
        <Button type="submit" size="lg" className="w-full" loading={busy}>Sign in</Button>
        <p className="text-center text-[14px]">
          <Link to="/forgot-password" className="text-ink-2 hover:text-ink hover:underline">Forgot your password?</Link>
        </p>
      </form>
    </AuthLayout>
  );
}

export function RegisterPage() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ display_name: "", email: "", password: "" });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [requested, setRequested] = useState<string | null>(null);
  if (user) return <Navigate to="/today" replace />;
  const fields = error instanceof ApiError ? error.fieldErrors() : {};

  if (requested) {
    return (
      <AuthLayout title="Thanks — your request is in" subtitle={requested}
        footer={<>Already approved? <Link to="/signin" className="font-semibold text-accent-text hover:underline">Sign in</Link></>}>
        <div role="status" className="space-y-3 text-[15px] text-ink-2">
          <p>We sent a confirmation to <span className="font-semibold text-ink">{form.email}</span>.</p>
          <p>Every new Person Learn account is reviewed by an administrator. Once yours is approved, you'll get an email and can sign in with the password you just chose.</p>
        </div>
      </AuthLayout>
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await register({ ...form, timezone: browserTimezone() });
      if (result.status === "pending") setRequested(result.message);
      else navigate("/onboarding", { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Create your account" subtitle="Personalized learning, from curiosity to mastery."
      footer={<>Already have an account? <Link to="/signin" className="font-semibold text-accent-text hover:underline">Sign in</Link></>}>
      <form onSubmit={submit} className="space-y-5" noValidate>
        <Field label="Your name" htmlFor="display_name" error={fields.display_name}>
          <Input id="display_name" autoComplete="name" required value={form.display_name}
            invalid={!!fields.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
        </Field>
        <Field label="Email" htmlFor="email" error={fields.email}>
          <Input id="email" type="email" autoComplete="email" required value={form.email} invalid={!!fields.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field label="Password" htmlFor="password" error={fields.password}
          hint="At least 10 characters. A short phrase works well.">
          <Input id="password" type="password" autoComplete="new-password" required value={form.password}
            invalid={!!fields.password} aria-describedby="password-hint"
            onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </Field>
        {error && !Object.keys(fields).length ? <InlineError error={error} /> : null}
        <Button type="submit" size="lg" className="w-full" loading={busy}>Create account</Button>
        <p className="text-[13px] text-ink-3">New accounts are reviewed by an administrator before first sign-in.</p>
        <p className="text-[13px] text-ink-3">
          Person Learn helps you build real knowledge through self-study. It doesn't award degrees or credentials.
        </p>
      </form>
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await post<{ message: string }>("/api/v1/auth/password-reset", { email });
      setSent(result.message);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Reset your password" subtitle="We'll email you a link to choose a new one."
      footer={<Link to="/signin" className="font-semibold text-accent-text hover:underline">Back to sign in</Link>}>
      {sent ? (
        <p role="status" className="rounded-xl bg-good-soft px-4 py-3 text-[15px] text-good">{sent}</p>
      ) : (
        <form onSubmit={submit} className="space-y-5" noValidate>
          <Field label="Email" htmlFor="email">
            <Input id="email" type="email" autoComplete="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <InlineError error={error} />
          <Button type="submit" size="lg" className="w-full" loading={busy}>Send reset link</Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.hash.slice(1)).get("token") ?? "";
  const [password, setPassword] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post("/api/v1/auth/password-reset/confirm", { token, new_password: password });
      window.history.replaceState(null, "", "/reset-password");
      setDone(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Choose a new password">
      {done ? (
        <div className="space-y-6">
          <p role="status" className="rounded-xl bg-good-soft px-4 py-3 text-[15px] text-good">
            Your password was changed. For your security, every device was signed out.
          </p>
          <Button size="lg" className="w-full" onClick={() => navigate("/signin")}>Sign in</Button>
        </div>
      ) : !token ? (
        <p className="text-[15px] text-ink-2">This reset link is incomplete. <Link className="font-semibold text-accent-text" to="/forgot-password">Request a new one</Link>.</p>
      ) : (
        <form onSubmit={submit} className="space-y-5" noValidate>
          <Field label="New password" htmlFor="new-password" hint="At least 10 characters.">
            <Input id="new-password" type="password" autoComplete="new-password" required value={password}
              onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <InlineError error={error} />
          <Button type="submit" size="lg" className="w-full" loading={busy}>Save new password</Button>
        </form>
      )}
    </AuthLayout>
  );
}
